import React, { useEffect, useRef, useState } from 'react';
import classnames from '../../../utils/classnames.tsx';
import { WorkistIcon, WorkistLogo } from '../../WorkistLogo.tsx';
import { NavigationContextProvider } from './NavigationContext.tsx';
import NavigationDocumentTypeItem from './NavigationDocumentTypeItem.tsx';
import NavigationDropDownItem from './NavigationDropDownItem.tsx';
import { useApplicationContext } from '../../../contexts/ApplicationContext.tsx';
import { useQuery } from '@apollo/client';
import { GET_NAVIGATION_CHANNELS } from './queries.ts';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import DropdownMenu from '../../DropdownMenu.tsx';
import useDarkMode from '../../../utils/useDarkMode.tsx';
import { navigationDocumentTypes } from './useNavigation.ts';
import { useTranslation } from 'react-i18next';
import { getPrivacyUrl } from '../../../../support/constants.ts';
import i18n from 'i18next';
import { withIcon } from '../../Icon.tsx';
import {
    faArrowLeftToLine,
    faArrowRightToLine,
    faArrowUpRightFromSquare,
    faChartColumn,
    faGear,
    faUser,
    faInbox,
    faRobot,
    faXmark,
} from '@fortawesome/pro-regular-svg-icons';
import { logout } from '../../../utils/authentication.ts';
import {
    canManageAllDocumentTypes,
    canManageCustomer,
    canManageIntegrations,
    canManageMasterData,
    canManageUsers,
    canSeeAnalytics,
    canSeeAssistants,
    canSeeDocuments,
    canSeeMailboxes,
} from '../../../../users/utils.ts';
import { canSeeSettings } from '../../SettingsNavigation.tsx';
import NavigationItem from './NavigationItem.tsx';
import WorkiNavButton from './WorkiNavButton.tsx';
import { CHAT_ENTRY_POINT } from '../../../../chat/constants.ts';
import { useControllableState } from '../../../utils/useControllableState.tsx';
import { useLayoutContext } from '../../../contexts/LayoutContext.tsx';
import { useEscapeKey } from '../../../utils/hooks/useEscapeKey.ts';
import { url } from '../../../utils/link.ts';
import { USER_PROFILE_PATH, USERS_PATH } from '../../../../users/constants.ts';
import { CUSTOMER_API_INTEGRATIONS_PATH, CUSTOMER_PROFILE_PATH } from '../../../../customer/constants.ts';
import { TEAMS_PATH } from '../../../../teams/constants.ts';
import { MASTER_DATA_PATH } from '../../../../masterdata/constants.ts';
import { validate as isValidUUID } from 'uuid';

interface NavigationProps {
    className?: string;
    initialActiveItem?: [string, string | null];
    activeItem?: [string, string | null];
    onSelect?: (item: [string, string | null]) => void;
    initialCollapsed?: boolean;
    collapsed?: boolean;
    onCollapsedChange?: (collapsed: boolean) => void;
}

export const useNavigationChannels = (options = {}) =>
    useQuery(GET_NAVIGATION_CHANNELS, {
        fetchPolicy: 'cache-first',
        notifyOnNetworkStatusChange: false,
        ...options,
    });

// Modules with /:prefix/:uuid routes that are NOT assistance pages — add new such modules here so
// they don't inherit the assistance page sidebar behavior (auto/click-outside collapse)
const NON_ASSISTANCE_PATH_PREFIXES = ['agents', 'master-data'];

const AnalyticsIcon = withIcon(faChartColumn);
const MailboxesIcon = withIcon(faInbox);
const AssistantsIcon = withIcon(faRobot);
const SettingsIcon = withIcon(faGear);
const UserIcon = withIcon(faUser);
const PanelRightCloseIcon = withIcon(faArrowRightToLine);
const PanelRightOpenIcon = withIcon(faArrowLeftToLine);
const CloseIcon = withIcon(faXmark);
const ExternalLinkIcon = withIcon(faArrowUpRightFromSquare);

const NavigationDivider = () => (
    <li aria-hidden="true" className="flex-none px-4 py-2">
        <div className="h-px bg-line-weak" />
    </li>
);

const Navigation = ({
    initialActiveItem = null,
    activeItem: propsActiveItem,
    onSelect: propsOnSelect,
    initialCollapsed = false,
    collapsed: propsCollapsed,
    onCollapsedChange: propsOnCollapsedChange,
    className,
}: NavigationProps) => {
    const { t } = useTranslation('assistance');
    const navigate = useNavigate();
    const { user, loading: applicationLoading } = useApplicationContext();

    // Check if currently in assistance page (pattern: /docType/recordId)
    const customer = user?.customer;
    const customerIsActive = customer.isActive;
    const location = useLocation();

    const { toggle: toggleDarkMode, isActive: isDarkModeActive } = useDarkMode();

    // active item
    const [activeItem, setActiveItem] = useControllableState(initialActiveItem, propsActiveItem, propsOnSelect);

    // those states are mainly used to figure out if sidebar should be collapsed
    const [manuallyCollapsed, setManuallyCollapsed] = useControllableState(
        initialCollapsed,
        propsCollapsed,
        propsOnCollapsedChange
    );

    // Check if currently in assistance page (pattern: /docType/recordId)
    const pathSegments = location.pathname.split('/').filter(Boolean);
    const isAssistancePage =
        pathSegments.length >= 2 &&
        isValidUUID(pathSegments[1]) &&
        !NON_ASSISTANCE_PATH_PREFIXES.includes(pathSegments[0]);
    const isMobile = useLayoutContext()?.isMobile ?? false;
    // small screens show the sidebar as an off-canvas drawer, which reuses the assistance collapse state
    const overlayMode = isAssistancePage || isMobile;
    // only the desktop assistance rail expands on tap and collapses on click outside
    const desktopAssistance = isAssistancePage && !isMobile;
    const [mouseOver, setMouseOver] = useState(false);
    const [focused, setFocused] = useState(false);
    const [dropdownOpen, setDropdownOpen] = useState(false);
    // button clicked will force collapse, it resets automatically after 200ms
    const [collapseButtonClicked, setCollapseButtonClicked] = useState(false);

    const collapsed =
        collapseButtonClicked || overlayMode
            ? manuallyCollapsed
            : !mouseOver && !focused && !dropdownOpen && manuallyCollapsed;

    const sidebarRef = useRef<HTMLElement>(null);

    useEffect(() => {
        // on small screens the backdrop closes the drawer instead, so taps inside portaled menus don't close it
        if (!desktopAssistance || manuallyCollapsed) return;

        const handleClickOutside = (event: MouseEvent) => {
            if (sidebarRef.current && !sidebarRef.current.contains(event.target as Node)) {
                setManuallyCollapsed(true);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [desktopAssistance, manuallyCollapsed]);

    // the drawer covers the page on small screens, so it closes after navigating
    useEffect(() => {
        if (isMobile) {
            setManuallyCollapsed(true);
        }
    }, [location.key, isMobile]);

    // an open menu inside the drawer takes the first Escape
    useEscapeKey(isMobile && !manuallyCollapsed && !dropdownOpen, () => setManuallyCollapsed(true));

    // the channel lists need the documents permission; without it there are no document entries below
    const {
        data: channelsData,
        previousData: channelsPreviousData, // prevent loader from showing if external refetch is triggered
        loading: channelsLoading,
    } = useNavigationChannels({ skip: !canSeeDocuments(user) });

    const showAssistants = canSeeAssistants(user);
    const showMailboxes = canSeeMailboxes(user);

    // Scroll shadow behavior

    const [showShadow, setShowShadow] = useState([false, false]);
    const listRef = useRef<HTMLUListElement | null>(null);

    const updateScroll = (container: HTMLUListElement) => {
        const { scrollTop, scrollHeight, clientHeight } = container;
        const isBottom = scrollTop + clientHeight >= scrollHeight;
        const isTop = scrollTop === 0;

        setShowShadow([!isTop, !isBottom]);
    };

    const handleScroll = (e: any) => {
        updateScroll(e.target);
    };

    // update shadows when resizing
    useEffect(() => {
        if (!listRef?.current) return;

        const handleResize = () => {
            // @ts-ignore
            updateScroll(listRef?.current);
        };

        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    useEffect(() => {
        if (!listRef?.current) return;

        if (!collapsed) {
            updateScroll(listRef?.current);
        }
    }, [collapsed]);

    return (
        <NavigationContextProvider collapsed={collapsed}>
            <div
                // this div is used to prevent the content from jumping when the sidebar is collapsed
                className={classnames(
                    'hidden md:block w-72 flex-none h-full z-10 transition-[width] duration-200 ease-in-out',
                    manuallyCollapsed && 'w-[74px]'
                )}
            />
            {isMobile && !manuallyCollapsed && (
                <div
                    className="absolute inset-0 z-40 bg-[rgba(0,0,0,0.5)] touch-none"
                    onClick={() => setManuallyCollapsed(true)}
                />
            )}
            <aside
                ref={sidebarRef}
                inert={isMobile && manuallyCollapsed}
                className={classnames(
                    'max-w-72 flex flex-none h-full w-72 select-none flex-col items-stretch bg-surface-weak border-r border-solid border-r-line-weak',
                    'transition-[width] duration-200 ease-in-out overflow-hidden absolute top-0 bottom-0 left-0 z-50 cursor-pointer',
                    'max-md:transition-transform max-md:duration-200',
                    collapsed && 'md:w-[74px] max-md:-translate-x-full',
                    className
                )}
                onClick={() => {
                    if (desktopAssistance && manuallyCollapsed) {
                        setManuallyCollapsed(false);
                    }
                }}
                onMouseEnter={() => !overlayMode && setMouseOver(true)}
                onMouseLeave={() => !overlayMode && setMouseOver(false)}
                onFocus={() => !overlayMode && setFocused(true)}
                onBlur={() => !overlayMode && setFocused(false)}
                onMouseUp={() => !overlayMode && setFocused(false)}
            >
                <div className="flex flex-none items-center p-4">
                    {!collapsed ? (
                        <>
                            <Link to="/">
                                <WorkistLogo className="h-7 w-auto text-content-strong" />
                            </Link>

                            <button
                                className="ml-auto rounded-md p-2 text-content-strong hover:bg-surface-strong cursor-pointer"
                                onClick={(event) => {
                                    event.stopPropagation();
                                    if (!manuallyCollapsed) {
                                        setCollapseButtonClicked(true);
                                        setTimeout(() => setCollapseButtonClicked(false), 200);
                                        setMouseOver(false);
                                    }
                                    setManuallyCollapsed(!manuallyCollapsed);
                                }}
                            >
                                {isMobile ? (
                                    <CloseIcon />
                                ) : manuallyCollapsed ? (
                                    <PanelRightCloseIcon />
                                ) : (
                                    <PanelRightOpenIcon />
                                )}
                            </button>
                        </>
                    ) : (
                        <Link to="/">
                            <WorkistIcon className="mt-0.5 ml-1 mb-0.5 h-7 w-auto text-content-strong" />
                        </Link>
                    )}
                </div>

                <nav className="flex min-h-0 flex-auto flex-col items-stretch relative">
                    <div
                        className={classnames(
                            'to-transparent absolute left-0 top-0 z-10 h-2 w-full flex-none bg-gradient-to-b from-tertiary opacity-0 transition-opacity duration-200',
                            showShadow[0] && 'opacity-100'
                        )}
                    />
                    <ul
                        className="flex min-h-0 flex-auto flex-col justify-start py-2 overflow-y-auto overflow-x-hidden"
                        onScroll={handleScroll}
                        ref={listRef}
                    >
                        {(canSeeDocuments(user) ? Object.entries(navigationDocumentTypes) : [])
                            .filter(([documentTypeName, documentType]) => {
                                const enabled =
                                    (customerIsActive && customer[documentType.enabledKey]) ||
                                    canManageAllDocumentTypes(user);
                                return enabled || !documentType.hideIfDisabled;
                            })
                            .map(([documentTypeName, documentType], idx) => {
                                const channels = channelsData?.[`${documentTypeName}ProcessingChannels`];
                                const loading =
                                    (channelsLoading && channelsPreviousData === undefined) || applicationLoading;
                                const onSelect = (event, subItem) => {
                                    if (desktopAssistance && manuallyCollapsed) {
                                        setManuallyCollapsed(false);
                                    } else {
                                        if (event?.metaKey || event?.ctrlKey) {
                                            // if cmd (Mac) or ctrl (Windows/Linux) is pressed during click, open in a new tab
                                            const paths = documentType?.paths;
                                            if (paths) {
                                                const itemUrl = subItem
                                                    ? url(paths.channelPath, { channelId: subItem })
                                                    : url(paths.overviewPath);
                                                const win = window.open(itemUrl, '_blank');
                                                win?.focus();
                                            }
                                        } else {
                                            // Otherwise activate the item in current tab
                                            setActiveItem([documentTypeName, subItem]);
                                        }
                                    }
                                };

                                const enabled =
                                    (customerIsActive && customer[documentType.enabledKey]) ||
                                    canManageAllDocumentTypes(user);
                                const active = activeItem?.[0] === documentTypeName ? activeItem[1] || true : false;
                                return (
                                    <NavigationDocumentTypeItem
                                        key={idx}
                                        label={t(documentType.label)}
                                        icon={<documentType.icon />}
                                        channels={channels}
                                        loading={loading}
                                        active={active}
                                        onSelect={onSelect}
                                        disabled={!enabled}
                                    />
                                );
                            })}
                        {canSeeDocuments(user) && (showAssistants || showMailboxes) && <NavigationDivider />}
                        {showAssistants && (
                            <NavigationItem
                                label={t('overview.navigation.assistants')}
                                icon={<AssistantsIcon />}
                                onSelect={() => navigate('/assistants')}
                                active={location.pathname.startsWith('/assistants')}
                                newBadge={t('overview.navigation.newBadge')}
                            />
                        )}
                        {showMailboxes && (
                            <NavigationItem
                                label={t('overview.navigation.mailboxes')}
                                icon={<MailboxesIcon />}
                                onSelect={() => navigate('/mailboxes')}
                                active={location.pathname.startsWith('/mailboxes')}
                                newBadge={t('overview.navigation.newBadge')}
                            />
                        )}
                        {canSeeAnalytics(user) && (
                            <>
                                <NavigationDivider />
                                <NavigationItem
                                    label={t('overview.navigation.analytics')}
                                    icon={<AnalyticsIcon />}
                                    onSelect={() => navigate('/analytics')}
                                    active={activeItem[0] === 'analytics'}
                                />
                            </>
                        )}
                    </ul>
                    <div
                        className={classnames(
                            'to-transparent absolute bottom-0 left-0 z-10 h-2 w-full flex-none bg-gradient-to-t from-tertiary opacity-0 transition-opacity duration-200',
                            showShadow[1] && 'opacity-100'
                        )}
                    />
                </nav>

                <ul className="flex flex-none flex-col items-stretch py-2">
                    <WorkiNavButton
                        onSelect={() => navigate('/chat', { state: { entryPoint: CHAT_ENTRY_POINT.SIDEBAR } })}
                        active={location.pathname.startsWith('/chat')}
                    />
                </ul>

                <nav className="flex flex-none flex-col items-stretch border-t border-solid border-line-weak py-2">
                    {canSeeSettings(user) && (
                        <NavigationDropDownItem
                            label={t('overview.navigation.settings')}
                            icon={<SettingsIcon />}
                            onOpenChange={setDropdownOpen}
                            loading={applicationLoading}
                            active={activeItem[0] === 'settings'}
                        >
                            {canManageCustomer(user) && (
                                <DropdownMenu.Item onSelect={() => navigate(CUSTOMER_PROFILE_PATH)}>
                                    {t('user:userTag.companyLink')}
                                </DropdownMenu.Item>
                            )}
                            {canManageUsers(user) && (
                                <DropdownMenu.Item onSelect={() => navigate(TEAMS_PATH)}>
                                    {t('user:userTag.teamsLink')}
                                </DropdownMenu.Item>
                            )}
                            {canManageUsers(user) && (
                                <DropdownMenu.Item onSelect={() => navigate(USERS_PATH)}>
                                    {t('user:userTag.usersLink')}
                                </DropdownMenu.Item>
                            )}

                            {(canManageCustomer(user) || canManageUsers(user)) &&
                                (canManageMasterData(user) || canManageIntegrations(user)) && (
                                    <DropdownMenu.Separator />
                                )}

                            {canManageMasterData(user) && (
                                <DropdownMenu.Item onSelect={() => navigate(MASTER_DATA_PATH)}>
                                    {t('user:userTag.masterDataLink')}
                                </DropdownMenu.Item>
                            )}
                            {canManageIntegrations(user) && (
                                <DropdownMenu.Item onSelect={() => navigate(CUSTOMER_API_INTEGRATIONS_PATH)}>
                                    {t('user:userTag.integrationsLink')}
                                </DropdownMenu.Item>
                            )}
                        </NavigationDropDownItem>
                    )}

                    <NavigationDropDownItem
                        label={user && `${user?.firstName} ${user?.lastName}`}
                        icon={<UserIcon />}
                        onOpenChange={setDropdownOpen}
                        loading={applicationLoading}
                        active={activeItem[0] === 'user'}
                    >
                        <div className="px-3 py-2 text-sm flex flex-col">
                            <span className="font-medium">{user && `${user?.firstName} ${user?.lastName}`}</span>
                            <span>{user && user.email}</span>
                        </div>
                        <DropdownMenu.Separator />
                        <DropdownMenu.Item onSelect={() => navigate(USER_PROFILE_PATH)}>
                            {t('user:userTag.profileLink')}
                        </DropdownMenu.Item>
                        <DropdownMenu.Item onSelect={toggleDarkMode}>
                            {t(`core:components.header.darkMode${isDarkModeActive ? 'Off' : 'On'}`)}
                        </DropdownMenu.Item>
                        <DropdownMenu.Separator />
                        <DropdownMenu.Item className="flex text-content-strong hover:text-content-strong" asChild>
                            <a href={getPrivacyUrl(i18n.language)} target="_blank">
                                {t(`overview.navigation.privacyPolicy`)}
                                <ExternalLinkIcon className="ml-auto" />
                            </a>
                        </DropdownMenu.Item>
                        <DropdownMenu.Separator />
                        <DropdownMenu.Item danger onClick={logout}>
                            {t('overview.navigation.logout')}
                        </DropdownMenu.Item>
                    </NavigationDropDownItem>
                </nav>
            </aside>
        </NavigationContextProvider>
    );
};

export default Navigation;
